/**
 * useClientPerformanceData Hook
 * Extracts and memoizes performance data transformations from ClientLevelView.
 * Converts raw ClientLevelData into PerformanceTab-compatible shape.
 */

import { useMemo } from 'react';
import type { ClientLevelData } from './useClientLevelData';
import type { PerformanceMetrics } from '../services/api/portfolioApiService';
import type { AdditionalSeries } from '../components/portfolio/molecules/PerformanceChart';

interface Contributor {
  name: string;
  contribution: number;
}

interface ClientPerformanceResult {
  metrics: PerformanceMetrics | null;
  attribution: {
    asset_allocation: number;
    security_selection: number;
    currency: number;
    other: number;
    total: number;
  } | null;
  history: any[];
  topContributors: Contributor[];
  bottomContributors: Contributor[];
  portfolioSeries: AdditionalSeries[];
}

const PORTFOLIO_COLORS = [
  '#10b981', // Green
  '#8b5cf6', // Purple
  '#f59e0b', // Amber
  '#ef4444', // Red
];

export function useClientPerformanceData(data: ClientLevelData | null): ClientPerformanceResult {
  return useMemo(() => {
    if (!data) {
      return {
        metrics: null,
        attribution: null,
        history: [],
        topContributors: [],
        bottomContributors: [],
        portfolioSeries: [],
      };
    }

    const perfCombined = data.performance?.combined;
    const riskCombined = data.risk?.combined;
    const perfBenchmark = data.performance?.benchmark;
    const perfAlpha = data.performance?.alpha;

    const metrics: PerformanceMetrics = {
      period: 'YTD',
      total_return: perfCombined?.ytd ?? null,
      annualized_return: perfCombined?.oneYear ?? null,
      ytd_return: perfCombined?.ytd ?? null,
      mtd_return: perfCombined?.mtd ?? null,
      qtd_return: perfCombined?.qtd ?? null,
      volatility: riskCombined?.volatility ?? null,
      sharpe_ratio: riskCombined?.sharpeRatio ?? null,
      max_drawdown: riskCombined?.maxDrawdown ?? null,
      benchmark_name: data.performance?.benchmarkName ?? null,
      benchmark_code: data.performance?.benchmarkCode ?? undefined,
      benchmark_return: perfBenchmark?.ytd ?? null,
      alpha: perfAlpha?.ytd ?? null,
      win_rate: null,
      information_ratio: (riskCombined?.volatility != null && riskCombined.volatility > 0 && perfAlpha?.ytd != null)
        ? perfAlpha.ytd / (riskCombined.volatility / 10)
        : null,
      tracking_error: riskCombined?.volatility != null ? riskCombined.volatility / 10 : null,
      active_share: null,
      current_drawdown: null,
      beta: null,
      sortino_ratio: null,
      calmar_ratio: (riskCombined?.maxDrawdown != null && riskCombined.maxDrawdown !== 0 && perfCombined?.oneYear != null)
        ? perfCombined.oneYear / Math.abs(riskCombined.maxDrawdown)
        : null,
    };

    // Use real attribution from backend if available
    const attribution = data.attribution ?? null;

    const history = data.performance?.history || [];

    const topContributors: Contributor[] = (data.performance?.topContributors ?? []).map(c => ({
      name: c.name,
      contribution: c.percentage ?? 0,
    }));

    const bottomContributors: Contributor[] = (data.performance?.bottomContributors ?? []).map(c => ({
      name: c.name,
      contribution: c.percentage ?? 0,
    }));

    // Only show per-portfolio lines when real per-portfolio history exists in the data
    // (i.e. history points contain portfolio1, portfolio2 keys from actual API data)
    const hasRealPortfolioHistory = history.length > 0 && history[0].portfolio1 !== undefined;
    const perfByPortfolio = data.performance?.byPortfolio ?? [];
    const portfolioSeries: AdditionalSeries[] = hasRealPortfolioHistory
      ? perfByPortfolio.map(
          (portfolio: { id: string; name: string }, index: number) => ({
            dataKey: `portfolio${index + 1}`,
            name: portfolio.name,
            color: PORTFOLIO_COLORS[index] || '#6b7280',
            strokeWidth: 1.5,
            strokeDasharray: undefined,
          })
        )
      : [];

    return { metrics, attribution, history, topContributors, bottomContributors, portfolioSeries };
  }, [data]);
}
